import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AGENCIES, TYPES } from '../mock';
import { fetchDossiers, imageUrl } from '../api';
import { Search, Filter, ArrowUpRight, Calendar, MapPin, X, Loader2 } from 'lucide-react';

export default function ArchivePage() {
  const [q, setQ] = useState('');
  const [agency, setAgency] = useState('ALL');
  const [era, setEra] = useState('ALL');
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchDossiers({ agency, q: debouncedQ, limit: 250 });
        if (!cancel) setDossiers(data.items);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [agency, debouncedQ]);

  const filtered = useMemo(() => {
    return dossiers.filter(d => {
      if (era !== 'ALL') {
        const y = parseInt((d.date || '').slice(-4)) || parseInt((d.date || '').slice(0, 4)) || 0;
        if (era === 'pre-1960' && y >= 1960) return false;
        if (era === '1960-79' && (y < 1960 || y > 1979)) return false;
        if (era === '1980-99' && (y < 1980 || y > 1999)) return false;
        if (era === '2000-19' && (y < 2000 || y > 2019)) return false;
        if (era === '2020+' && y < 2020) return false;
      }
      return true;
    });
  }, [dossiers, era]);

  return (
    <div>
      <div className="relative border-b border-white/5">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="mx-auto max-w-[1400px] px-6 py-7 relative">
          <div className="mono text-[10px] tracking-[0.2em] text-cyan-300/90 uppercase mb-2">PURSUE · Release 01 · V1.0 FR · Données officielles war.gov</div>
          <h1 className="serif text-3xl md:text-4xl">Archive <span className="italic text-cyan-300">Déclassifiée</span></h1>
          <p className="text-2 mt-2 max-w-3xl text-sm">160 dossiers officiels traduits automatiquement par IA. Mise à jour 2×/jour depuis war.gov/UFO/.</p>

          <div className="mt-4 flex items-center gap-2 glass rounded-md px-3 py-2 border border-white/10 focus-within:border-cyan-400/40 transition-colors">
            <Search className="h-3.5 w-3.5 text-3" />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="ovni, infrarouge, témoin, radar, soucoupe…"
                   className="flex-1 bg-transparent outline-none placeholder:text-4 text-1 mono text-xs" />
            {q && <button onClick={()=>setQ('')} className="text-3 hover:text-1"><X className="h-3.5 w-3.5" /></button>}
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" /> :
              <span className="mono text-[9px] text-3 px-1.5 py-0.5 border border-white/10 rounded">{filtered.length} résultats</span>}
          </div>

          <div className="mt-3 flex flex-wrap items-start gap-x-5 gap-y-2">
            <FilterGroup label="Agence">
              <Chip active={agency==='ALL'} onClick={()=>setAgency('ALL')} label="Toutes" count={dossiers.length} />
              {AGENCIES.map(a => (
                <Chip key={a.id} active={agency===a.id} onClick={()=>setAgency(a.id)}
                      label={a.short} count={a.count} color={a.color} />
              ))}
            </FilterGroup>
            <FilterGroup label="Époque">
              {[{ id:'ALL', label:'Toutes' }, { id:'pre-1960', label:'Avant 1960' }, { id:'1960-79', label:'1960–79' }, { id:'1980-99', label:'1980–99' }, { id:'2000-19', label:'2000–19' }, { id:'2020+', label:'2020+' }].map(e => (
                <Chip key={e.id} active={era===e.id} onClick={()=>setEra(e.id)} label={e.label} />
              ))}
            </FilterGroup>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-6">
        {loading && filtered.length === 0 ? (
          <div className="text-center py-16 text-3 inline-flex items-center justify-center gap-2 w-full"><Loader2 className="h-4 w-4 animate-spin" /> Chargement des dossiers…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-3">Aucun dossier ne correspond à votre recherche.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map(d => {
              const agencyMeta = AGENCIES.find(a => a.id === d.agency);
              return (
                <Link key={d.id} to={`/dossier/${d.id}`} className="doc-card glass rounded-lg overflow-hidden">
                  <div className="relative aspect-[16/9] overflow-hidden bg-black">
                    {d.image_url && (
                      <img src={imageUrl(d.image_url)} alt={d.title} className="absolute inset-0 w-full h-full object-cover opacity-85 transition-transform duration-500 hover:scale-105" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                    <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                      <span className="badge-classif" style={{ color: agencyMeta?.color }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: agencyMeta?.color }} /> {agencyMeta?.short}
                      </span>
                      <span className="mono text-[9px] text-2 bg-black/60 px-1.5 py-0.5 rounded">{d.classification}</span>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="mono text-[9px] text-3 mb-1 flex items-center gap-2 truncate">
                      <span className="truncate">{d.id.split('-').slice(0, 2).join('-')}</span>
                      {d.date && <span className="inline-flex items-center gap-1"><Calendar className="h-2.5 w-2.5" /> {d.date}</span>}
                    </div>
                    <div className="text-1 text-sm font-medium leading-snug mb-1.5 line-clamp-2">{d.title}</div>
                    <p className="text-3 text-xs line-clamp-2">{d.summary}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                      <span className="mono text-[9px] text-3 inline-flex items-center gap-1 truncate"><MapPin className="h-2.5 w-2.5" /> {d.location || 'N/A'}</span>
                      <span className="text-cyan-300 text-[11px] inline-flex items-center gap-1 shrink-0">Ouvrir <ArrowUpRight className="h-3 w-3" /></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="mono text-[9px] tracking-widest text-3 uppercase mb-1 inline-flex items-center gap-1"><Filter className="h-2.5 w-2.5" /> {label}</div>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}
function Chip({ active, onClick, label, count, color }) {
  return (
    <button onClick={onClick}
            className={`mono text-[10px] px-2 py-0.5 rounded border transition-colors inline-flex items-center gap-1 ${active ? 'bg-cyan-400/15 border-cyan-300/40 text-cyan-100' : 'border-white/10 text-2 hover:bg-white/5'}`}>
      {color && <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />}
      {label}
      {typeof count === 'number' && <span className="text-3">{count}</span>}
    </button>
  );
}
